import { useEffect, useRef, useState } from "react";
import type { ComponentType } from "react";
import {
    Leaf,
    Sprout,
    Droplet,
    CloudRain,
    Mountain,
    Sun,
    TreePine,
    Wheat,
    FlaskConical,
} from "lucide-react";

type IconType = ComponentType<{ className?: string }>;

const BASE_ICONS: IconType[] = [
    Leaf, // vegetação
    Sprout, // germinação
    Droplet, // umidade
    CloudRain, // clima
    Mountain, // relevo
    Sun, // insolação
    TreePine, // cobertura vegetal
    Wheat, // cultura
    FlaskConical, // análise laboratorial
];

// dobra a quantidade (repete o conjunto) para um fundo mais povoado
const ICONS: IconType[] = [...BASE_ICONS, ...BASE_ICONS];

const NODE_COUNT = ICONS.length;
const MAX_PAIRS = (NODE_COUNT * (NODE_COUNT - 1)) / 2;
const PADDING = 48;
const SPEED = 14; // px por segundo
const MIN_DIST = 222; // distância mínima entre os ícones (não se encostam)
const LINK_DISTANCE = 320; // distância p/ desenhar a linha de ligação

// --- malha de triângulos tesselados (low-poly) ---------------------------
function mulberry32(seed: number) {
    let a = seed;
    return () => {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

// tons suaves (claros e verdes translúcidos) com pouco contraste sobre o fundo
const TRIANGLE_RGB = [
    "255,255,255",
    "220,240,205",
    "188,224,163",
    "150,204,120",
    "110,178,84",
    "70,140,55",
];

const SIDE = 54; // lado do triângulo (px)

type Tri = { points: string; fill: string; stroke: string };

function buildTriangles(w: number, h: number): Tri[] {
    if (w === 0 || h === 0) return [];
    const rng = mulberry32(20260601);
    const hStep = (SIDE * Math.sqrt(3)) / 2;
    const half = SIDE / 2;
    const diag = Math.hypot(w, h);
    const reach = diag * 0.5; // até onde a malha avança a partir de cada canto
    const tris: Tri[] = [];

    const rows = Math.ceil(h / hStep) + 1;
    const cols = Math.ceil(w / SIDE) + 2;

    for (let r = -1; r <= rows; r++) {
        const yTop = r * hStep;
        const yBot = yTop + hStep;
        const off = (((r % 2) + 2) % 2) * half;

        for (let i = -1; i <= cols; i++) {
            const tx = off + i * SIDE;
            const txn = off + (i + 1) * SIDE;
            const bx = off + i * SIDE + half;
            const bxn = off + (i + 1) * SIDE + half;

            // dois triângulos por célula: um apontando p/ baixo, outro p/ cima
            const cells: [number, number][][] = [
                [
                    [tx, yTop],
                    [txn, yTop],
                    [bx, yBot],
                ],
                [
                    [txn, yTop],
                    [bx, yBot],
                    [bxn, yBot],
                ],
            ];

            for (const pts of cells) {
                const cx = (pts[0][0] + pts[1][0] + pts[2][0]) / 3;
                const cy = (pts[0][1] + pts[1][1] + pts[2][1]) / 3;
                // mais denso/forte perto dos cantos da diagonal, limpo no centro
                const d = Math.min(Math.hypot(cx, cy), Math.hypot(cx - w, cy - h));
                const weight = 1 - d / reach;
                if (weight <= 0.03) continue;

                const points = pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
                const filled = rng() < weight * 0.95;
                let fill = "transparent";
                if (filled) {
                    const rgb = TRIANGLE_RGB[Math.floor(rng() * TRIANGLE_RGB.length)];
                    const alpha = Math.min(0.9, (0.22 + 0.45 * weight) * (0.6 + 0.4 * rng()));
                    fill = `rgba(${rgb},${alpha.toFixed(3)})`;
                }
                const strokeAlpha = (0.06 + 0.1 * weight).toFixed(3);
                tris.push({
                    points,
                    fill,
                    stroke: `rgba(255,255,255,${strokeAlpha})`,
                });
            }
        }
    }
    return tris;
}

export default function ConstellationBackground() {
    const containerRef = useRef<HTMLDivElement>(null);
    const nodeRefs = useRef<(HTMLDivElement | null)[]>([]);
    const innerRefs = useRef<(HTMLDivElement | null)[]>([]);
    const lineRefs = useRef<(SVGLineElement | null)[]>([]);
    const [tris, setTris] = useState<Tri[]>([]);
    const [dims, setDims] = useState({ w: 0, h: 0 });

    // malha de triângulos (recalculada conforme o tamanho da tela)
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const build = () => {
            const w = container.clientWidth;
            const h = container.clientHeight;
            setDims({ w, h });
            setTris(buildTriangles(w, h));
        };
        build();
        const ro = new ResizeObserver(build);
        ro.observe(container);
        return () => ro.disconnect();
    }, []);

    // ícones flutuantes + interação com o cursor (animação suave via DOM)
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let width = container.clientWidth;
        let height = container.clientHeight;
        let rect = container.getBoundingClientRect();

        const nodes = Array.from({ length: NODE_COUNT }, () => {
            const angle = Math.random() * Math.PI * 2;
            return {
                x: PADDING + Math.random() * Math.max(1, width - PADDING * 2),
                y: PADDING + Math.random() * Math.max(1, height - PADDING * 2),
                vx: Math.cos(angle) * SPEED,
                vy: Math.sin(angle) * SPEED,
                // giro da direção (rad/s): no toque os ícones fazem curvas
                // contínuas em vez de linhas retas.
                turn: (Math.random() < 0.5 ? -1 : 1) * (0.25 + Math.random() * 0.45),
                scale: 1,
                ts: 1,
            };
        });

        // Celular/tablet não tem cursor: o toque gera um "mousemove" que
        // deixava o ponteiro ativo para sempre e prendia os ícones no ponto
        // tocado. Lá a animação é só automática (curvas + velocidade maior).
        const canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
        const speedFactor = canHover ? 1 : 1.8;

        const pointer = { x: 0, y: 0, active: false };
        const onMove = (e: MouseEvent) => {
            if (!canHover) return;
            pointer.x = e.clientX - rect.left;
            pointer.y = e.clientY - rect.top;
            pointer.active = true;
        };
        const onOut = (e: MouseEvent) => {
            if (!e.relatedTarget) pointer.active = false;
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseout", onOut);

        const ro = new ResizeObserver(() => {
            width = container.clientWidth;
            height = container.clientHeight;
            rect = container.getBoundingClientRect();
        });
        ro.observe(container);

        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        const RADIUS = 340; // raio de influência do cursor
        const FOLLOW = 1.6; // intensidade do "seguir o cursor"

        let raf = 0;
        let last = performance.now();

        const frame = (now: number) => {
            const dt = Math.min((now - last) / 1000, 0.05);
            last = now;

            // Densidade conforme a área: em telas pequenas não cabem 18 ícones
            // a 222px uns dos outros — a separação os empurrava todos contra
            // as bordas (um "retângulo" de ícones). Reduz a distância mínima e
            // a quantidade de ícones visíveis para caber na área disponível.
            const minDist = Math.min(MIN_DIST, Math.max(90, Math.min(width, height) * 0.35));
            const linkDist = minDist * (LINK_DISTANCE / MIN_DIST);
            const area = Math.max(1, (width - PADDING * 2) * (height - PADDING * 2));
            const density = canHover ? 1 : 1.4; // toque: ícones mais espaçados
            const active = Math.min(NODE_COUNT, Math.max(4, Math.floor(area / (minDist * minDist * density))));

            // nó mais próximo do cursor (destaque)
            let nearest = -1;
            let nearestD = Infinity;
            if (pointer.active) {
                for (let i = 0; i < active; i++) {
                    const dx = nodes[i].x - pointer.x;
                    const dy = nodes[i].y - pointer.y;
                    const d = dx * dx + dy * dy;
                    if (d < nearestD) {
                        nearestD = d;
                        nearest = i;
                    }
                }
            }

            // movimento base + atração ao cursor
            for (let i = 0; i < active; i++) {
                const n = nodes[i];
                if (!reduceMotion) {
                    if (!canHover) {
                        const a = n.turn * dt;
                        const cos = Math.cos(a);
                        const sin = Math.sin(a);
                        const vx = n.vx * cos - n.vy * sin;
                        n.vy = n.vx * sin + n.vy * cos;
                        n.vx = vx;
                    }
                    n.x += n.vx * speedFactor * dt;
                    n.y += n.vy * speedFactor * dt;
                }

                let targetScale = 1;
                if (pointer.active) {
                    const dx = pointer.x - n.x;
                    const dy = pointer.y - n.y;
                    const dist = Math.hypot(dx, dy);
                    const prox = Math.max(0, 1 - dist / RADIUS);
                    n.x += dx * prox * FOLLOW * dt;
                    n.y += dy * prox * FOLLOW * dt;
                    targetScale = 1 + prox * 0.25;
                    if (i === nearest) {
                        const ease = Math.min(1, 6 * dt);
                        n.x += dx * ease;
                        n.y += dy * ease;
                        targetScale = 1.7;
                    }
                }
                n.ts = targetScale;
            }

            // separação: mantém uma distância mínima entre os ícones
            for (let i = 0; i < active; i++) {
                for (let j = i + 1; j < active; j++) {
                    const a = nodes[i];
                    const b = nodes[j];
                    let dx = b.x - a.x;
                    let dy = b.y - a.y;
                    let dist = Math.hypot(dx, dy);
                    if (dist === 0) {
                        dx = 0.7071;
                        dy = 0.7071;
                        dist = 1;
                    }
                    if (dist < minDist) {
                        const push = (minDist - dist) / 2;
                        const nx = dx / dist;
                        const ny = dy / dist;
                        if (i === nearest) {
                            b.x += nx * push * 2;
                            b.y += ny * push * 2;
                        } else if (j === nearest) {
                            a.x -= nx * push * 2;
                            a.y -= ny * push * 2;
                        } else {
                            a.x -= nx * push;
                            a.y -= ny * push;
                            b.x += nx * push;
                            b.y += ny * push;
                        }
                    }
                }
            }

            // limites + escala + transform
            for (let i = 0; i < nodes.length; i++) {
                const n = nodes[i];
                const el = nodeRefs.current[i];
                if (i >= active) {
                    if (el) el.style.display = "none";
                    continue;
                }
                if (el) el.style.display = "";
                if (n.x <= PADDING) {
                    n.x = PADDING;
                    n.vx = Math.abs(n.vx);
                } else if (n.x >= width - PADDING) {
                    n.x = width - PADDING;
                    n.vx = -Math.abs(n.vx);
                }
                if (n.y <= PADDING) {
                    n.y = PADDING;
                    n.vy = Math.abs(n.vy);
                } else if (n.y >= height - PADDING) {
                    n.y = height - PADDING;
                    n.vy = -Math.abs(n.vy);
                }

                n.scale += (n.ts - n.scale) * Math.min(1, 8 * dt);

                if (el) {
                    el.style.transform = `translate3d(${n.x}px, ${n.y}px, 0)`;
                    el.style.zIndex = i === nearest ? "5" : "1";
                    el.style.filter =
                        i === nearest ? "drop-shadow(0 0 10px rgba(255,255,255,0.65))" : "none";
                }
                const inner = innerRefs.current[i];
                if (inner) {
                    inner.style.transform = `translate(-50%, -50%) scale(${n.scale.toFixed(3)})`;
                }
            }

            // linhas de ligação entre ícones próximos
            let li = 0;
            for (let i = 0; i < nodes.length; i++) {
                for (let j = i + 1; j < nodes.length; j++) {
                    const line = lineRefs.current[li++];
                    if (!line) continue;
                    if (i >= active || j >= active) {
                        line.setAttribute("stroke-opacity", "0");
                        continue;
                    }
                    const dx = nodes[i].x - nodes[j].x;
                    const dy = nodes[i].y - nodes[j].y;
                    const dist = Math.hypot(dx, dy);
                    if (dist < linkDist) {
                        line.setAttribute("x1", String(nodes[i].x));
                        line.setAttribute("y1", String(nodes[i].y));
                        line.setAttribute("x2", String(nodes[j].x));
                        line.setAttribute("y2", String(nodes[j].y));
                        line.setAttribute("stroke-opacity", String((1 - dist / linkDist) * 0.4));
                    } else {
                        line.setAttribute("stroke-opacity", "0");
                    }
                }
            }

            raf = requestAnimationFrame(frame);
        };

        raf = requestAnimationFrame(frame);

        return () => {
            ro.disconnect();
            cancelAnimationFrame(raf);
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseout", onOut);
        };
    }, []);

    return (
        <div
            ref={containerRef}
            className="absolute inset-0 overflow-hidden pointer-events-none opacity-60 md:opacity-100"
            aria-hidden="true"
        >
            {/* malha de triângulos */}
            <svg
                className="absolute inset-0 opacity-10"
                width={dims.w}
                height={dims.h}
                viewBox={`0 0 ${dims.w} ${dims.h}`}
            >
                {tris.map((t, idx) => (
                    <polygon key={idx} points={t.points} fill={t.fill} stroke={t.stroke} strokeWidth={1} />
                ))}
            </svg>

            {/* linhas que interligam os ícones próximos */}
            <svg className="absolute inset-0 h-full w-full">
                {Array.from({ length: MAX_PAIRS }, (_, idx) => (
                    <line
                        key={idx}
                        ref={(el) => {
                            lineRefs.current[idx] = el;
                        }}
                        stroke="white"
                        strokeWidth={1}
                        strokeOpacity={0}
                    />
                ))}
            </svg>

            {/* nós (ícones do tema) */}
            {ICONS.map((Icon, idx) => (
                <div
                    key={idx}
                    ref={(el) => {
                        nodeRefs.current[idx] = el;
                    }}
                    className="absolute left-0 top-0 will-change-transform"
                >
                    <div
                        ref={(el) => {
                            innerRefs.current[idx] = el;
                        }}
                        style={{ transform: "translate(-50%, -50%)" }}
                    >
                        <span
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 shadow-lg ring-1 ring-white/30 backdrop-blur-sm"
                            style={{
                                animation: `terraflow-node-pulse 3s ease-in-out ${(idx % 5) * 0.4}s infinite`,
                            }}
                        >
                            <Icon className="h-4 w-4 text-white" />
                        </span>
                    </div>
                </div>
            ))}

            <style>{`
        @keyframes terraflow-node-pulse {
          0%, 100% { transform: scale(1); opacity: 0.85; }
          50% { transform: scale(1.12); opacity: 1; }
        }
      `}</style>
        </div>
    );
}
